/**
 * Business logic for RITA / EU reference tables (which bundles to load, manifest URLs, merging).
 * Types / caches: `constants.ts`. Network: `requests.js`.
 */
import { EuRefTablesTypesAll, RitaRefTablesTypesAll } from 'src/constants/referenceTables';

export function refTablesUploadDateString() {
  return new Date().toISOString().substring(0, 10);
}

export function normalizeEuInterfaceLanguage(language) {
  return typeof language === 'string' && language.length ? language : 'en';
}

export function dataPathUrlForRefBundle(swiftRelativePath) {
  return `/data/${swiftRelativePath}`;
}

/**
 *
 * @param {Array<{ type: string, path: string }>} documents
 * @param {string[]} typesToFetch
 * @param {Record<string, string>} cacheNameByType
 * @param {(ctx: { type: string, dataURL: string, cacheName: string }) => Promise<object|undefined|null>} loadSubset
 * @returns {Promise<Record<string, unknown>>}
 */
export async function mergeRefTableBundlesFromManifest(documents, typesToFetch, cacheNameByType, loadSubset) {
  const finalData = {};
  for (const type of typesToFetch) {
    const doc = documents.find((d) => d.type === type);
    if (!doc || !doc.path) continue;
    const dataURL = dataPathUrlForRefBundle(doc.path);
    const cacheName = cacheNameByType[type];
    const subset = await loadSubset({ type, dataURL, cacheName });
    if (!subset || subset.error) continue;
    Object.assign(finalData, subset);
  }
  return finalData;
}

/**
 * Normalizes the list of requested RITA types.
 * @param {string[]} [requestedTypes]
 * @returns {string[]}
 */
function normalizeRequestedRitaTypes(requestedTypes) {
  const allowed = new Set(RitaRefTablesTypesAll);
  if (!Array.isArray(requestedTypes) || requestedTypes.length === 0) {
    return [...RitaRefTablesTypesAll];
  }
  return requestedTypes.filter((t) => allowed.has(t));
}

/**
 * Returns RITA bundles to load for a given declaration type (e.g. delta_g_import -> common only).
 * @param {string} [declarationType]
 * @returns {string[]}
 */
export function ritaRefTypesForDeclarationType(declarationType) {
  if (!declarationType || typeof declarationType !== 'string') {
    return normalizeRequestedRitaTypes();
  }
  const lower = declarationType.toLowerCase();
  if (lower === 'delta_t' || lower.startsWith('delta_t_')) {
    return normalizeRequestedRitaTypes(['common', 'delta_t']);
  }
  if (lower === 'delta_e' || lower.startsWith('delta_e_')) {
    return normalizeRequestedRitaTypes(['common', 'delta_e']);
  }
  if (lower === 'delta_i' || lower.startsWith('delta_i') || lower === 'delta_ie' || lower.startsWith('delta_ie_')) {
    return normalizeRequestedRitaTypes(['common', 'delta_i']);
  }
  return normalizeRequestedRitaTypes(['common']);
}

/** Normalizes the list of requested EU types (bundles produced by ETL: delta_t, common). */
function normalizeEuRefTypes(requestedTypes) {
  const allowed = new Set(EuRefTablesTypesAll);
  if (!Array.isArray(requestedTypes) || requestedTypes.length === 0) {
    return [...EuRefTablesTypesAll];
  }
  return requestedTypes.filter((t) => allowed.has(t));
}

/**
 * Returns EU Swift bundles to load by declaration family (same logic as `ritaRefTypesForDeclarationType`).
 * @param {string} [declarationType]
 * @returns {string[]}
 */
export function euRefTypesForDeclarationType(declarationType) {
  if (!declarationType || typeof declarationType !== 'string') {
    return normalizeEuRefTypes();
  }
  const lower = declarationType.toLowerCase();
  if (lower === 'delta_t' || lower.startsWith('delta_t_')) {
    return normalizeEuRefTypes(['common', 'delta_t']);
  }
  if (
    lower === 'delta_e' ||
    lower.startsWith('delta_e_') ||
    lower === 'delta_i' ||
    lower.startsWith('delta_i') ||
    lower === 'delta_ie' ||
    lower.startsWith('delta_ie_')
  ) {
    return normalizeEuRefTypes(['common', 'delta_t']);
  }
  return normalizeEuRefTypes(['common', 'delta_t']);
}

/**
 * @param {string[]} [requestedTypes]
 * @returns {string[]}
 */
export function normalizeRitaRefTableTypesForFetch(requestedTypes) {
  return normalizeRequestedRitaTypes(requestedTypes);
}

/**
 * @param {string[]} [requestedTypes]
 * @returns {string[]}
 */
export function normalizeEuRefTableTypesForFetch(requestedTypes) {
  return normalizeEuRefTypes(requestedTypes);
}
